import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Shield, Mail, ArrowLeft } from "lucide-react";

/* ============================================================================
 * Privacy Policy — required by App Store Review (5.1.1) and Google Play.
 * The Apple Sign In + Google Sign In flows also require a publicly hosted
 * Privacy Policy URL declared in the Developer / App Store Connect record.
 *
 * Effective date is the day we ship this page publicly. Bump the constant
 * (and add a "Changes" subsection at the bottom) any time the substantive
 * content changes.
 * ========================================================================== */

const EFFECTIVE_DATE = "May 29, 2026";
const VERSION = "2.0";
const PRIVACY_EMAIL = "privacy@ealoom.com";
const COMPANY_LEGAL_NAME = "Lambda Flow LLC";
const COMPANY_BRAND = "Ealoom";
const POSTAL_ADDRESS_LINE_1 = "1209 Orange Street";
const POSTAL_ADDRESS_LINE_2 = "Wilmington, DE 19801";
const POSTAL_ADDRESS_COUNTRY = "United States";

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-10">
      <h2 className="text-xl font-semibold text-slate-900 mb-3">{title}</h2>
      <div className="space-y-3 text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}

function Privacy() {
  const location = useLocation();

  useEffect(() => {
    const isExtensionRoute = location.pathname.endsWith("/extension");
    const targetId = isExtensionRoute
      ? "browser-extension"
      : location.hash.replace(/^#/, "");
    if (!targetId) return;
    const el = document.getElementById(targetId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.pathname, location.hash]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to home
        </Link>

        <header className="mt-6 flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
            <Shield className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Privacy Policy</h1>
            <p className="text-slate-500 mt-1 text-sm">
              Effective {EFFECTIVE_DATE} · Version {VERSION} ·{" "}
              {COMPANY_LEGAL_NAME} (d/b/a {COMPANY_BRAND})
            </p>
          </div>
        </header>

        <p className="mt-8 text-slate-700 leading-relaxed">
          This policy explains what information {COMPANY_BRAND} collects, how
          we use it, and the choices you have. It covers the {COMPANY_BRAND}{" "}
          web dispatch console at <span className="font-mono">www.ealoom.com</span>,
          the {COMPANY_BRAND} Driver mobile app for iOS and Android, and the{" "}
          <Link
            to="/privacy/extension"
            className="text-blue-600 underline"
          >
            {COMPANY_BRAND} Container Monitor Chrome extension
          </Link>{" "}
          (collectively, the &ldquo;Service&rdquo;).
        </p>
        <p className="mt-3 text-slate-700 leading-relaxed">
          {COMPANY_BRAND} is a multi-tenant SaaS product for the trucking
          industry. Each trucking company that signs up (the &ldquo;tenant&rdquo;)
          controls its own dispatchers, drivers, and operational data. We
          process data on behalf of each tenant under their terms of service
          with us. All data collected by the apps is transmitted over HTTPS to
          our servers; nothing is stored only on-device except a short-lived
          offline GPS buffer that is cleared on the next sync.
        </p>

        <Section id="information-we-collect" title="1. Information we collect">
          <p>
            <strong>Account information.</strong> When you create an account
            we collect your email, display name, and (if you choose password
            login) a hashed password. If you use Sign in with Google or Sign in
            with Apple we receive the email and display name those providers
            return; we never see your provider password.
          </p>
          <p>
            <strong>Driver profile data.</strong> For driver accounts, the
            mobile app collects information you submit during onboarding:
            phone number, mailing address, CDL number / class / expiration,
            medical card, TWIC card, drug-test document, insurance document,
            EIN document (for owner-operators), business name, and emergency
            contact. Document images are stored encrypted in our cloud
            storage. Profile photos you upload are stored similarly.
          </p>
          <p>
            <strong>Location data (precise GPS).</strong> While your driver
            status is set to &ldquo;On Duty&rdquo;, the mobile app collects
            your precise GPS coordinates approximately every 10 seconds and
            transmits them to your dispatcher &mdash; that is, authorized
            personnel of the trucking company you are associated with. This is
            the app&rsquo;s core function. Specifically:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Coordinates are collected <strong>only</strong> while you are On Duty.</li>
            <li>
              Collection runs through an Android foreground service so it
              continues while the app is minimized or the screen is off &mdash;
              see Section 2 for the persistent notification.
            </li>
            <li>
              Coordinates are sent <strong>only</strong> to your trucking
              company&rsquo;s dispatchers, accountants, and managers (your
              &ldquo;tenant&rdquo;). They are <strong>not</strong> shared with
              any third party for advertising, analytics, or any other purpose.
            </li>
            <li>
              We retain GPS location history for <strong>24 months</strong>,
              then delete it from production systems.
            </li>
            <li>
              We do <strong>not</strong> collect GPS when you are Off Duty,
              when you have signed out, or when location permission is denied
              in system settings.
            </li>
          </ul>
          <p>
            <strong>Operational data.</strong> Tasks, container numbers,
            appointment times, port / warehouse selections, photos taken
            during delivery (port ticket, container exterior, seal, BOL),
            chat messages between dispatcher and driver, and timestamps of
            workflow events (entered terminal, picked container, returned to
            yard, etc.).
          </p>
          <p>
            <strong>Push notification token.</strong> When you install the
            Driver app we obtain a Firebase Cloud Messaging (FCM) token
            associated with your account so we can deliver task assignments,
            chat messages, and route changes. The token is a device identifier
            for delivery routing only; it is regenerated by the operating
            system if you reinstall or clear app data.
          </p>
          <p>
            <strong>Device &amp; diagnostic data.</strong> We collect device
            model, OS version, app version, language preference, and a Firebase
            installation ID. Crash reports and performance traces are
            collected via Firebase Crashlytics. These reports never contain
            account passwords, GPS coordinates, or chat payload contents.
          </p>
          <p>
            <strong>Camera and photos.</strong> The app accesses your camera
            and photo library only when you initiate it &mdash; to capture a
            delivery photo, attach a chat image, or upload a document. We
            never access them in the background.
          </p>
        </Section>

        <Section
          id="permissions-and-foreground-service"
          title="2. Permissions and the foreground service"
        >
          <p>
            The Driver mobile app requests the following permissions; each is
            used only for the purpose listed below and only after you grant it
            through the system permission dialog.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border border-slate-200">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left px-3 py-2 border-b border-slate-200">
                    Permission
                  </th>
                  <th className="text-left px-3 py-2 border-b border-slate-200">
                    Why it is required
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-3 py-2 border-b border-slate-100 font-mono">
                    Precise / approximate location
                  </td>
                  <td className="px-3 py-2 border-b border-slate-100">
                    Live GPS to your dispatcher while you are On Duty
                  </td>
                </tr>
                <tr>
                  <td className="px-3 py-2 border-b border-slate-100 font-mono">
                    Camera
                  </td>
                  <td className="px-3 py-2 border-b border-slate-100">
                    Capture delivery, container, seal, and document photos
                  </td>
                </tr>
                <tr>
                  <td className="px-3 py-2 border-b border-slate-100 font-mono">
                    Foreground service (location)
                  </td>
                  <td className="px-3 py-2 border-b border-slate-100">
                    Keep GPS flowing while the app is minimized or the screen
                    is off
                  </td>
                </tr>
                <tr>
                  <td className="px-3 py-2 border-b border-slate-100 font-mono">
                    Notifications (POST_NOTIFICATIONS)
                  </td>
                  <td className="px-3 py-2 border-b border-slate-100">
                    Task assignments, chat messages, route changes, and the
                    persistent tracking notification described below
                  </td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono">Internet, Wake Lock</td>
                  <td className="px-3 py-2">
                    Network communication with our servers and keeping the
                    GPS pipeline alive during a task
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            <strong>Persistent tracking notification.</strong> While you are
            On Duty, the app runs an Android foreground service that displays
            a persistent notification reading &ldquo;Location tracking
            active&rdquo; (or similar). Android keeps this notification visible
            for as long as tracking is on, so you always know GPS is being
            collected. Tracking continues even if you minimize the app or the
            screen turns off, and stops as soon as you (a) switch to Off Duty,
            (b) sign out, or (c) revoke location permission in system
            settings.
          </p>
          <p>
            <strong>Background location.</strong> The app does{" "}
            <strong>not</strong> request the Android{" "}
            <span className="font-mono">ACCESS_BACKGROUND_LOCATION</span>{" "}
            permission. GPS is only collected through the foreground service
            described above; if the foreground service stops (e.g., the system
            kills the app), tracking pauses until you re-open the app.
          </p>
        </Section>

        <Section id="how-we-use-it" title="3. How we use the information">
          <ul className="list-disc pl-5 space-y-2">
            <li>To authenticate you and keep your session secure.</li>
            <li>
              To route tasks between the dispatcher and the assigned driver
              and to compute ETAs and route distance.
            </li>
            <li>
              To generate invoices, driver statements, and payment records
              for the tenant company you belong to.
            </li>
            <li>
              To send push notifications about new task assignments, chat
              messages, route changes, and other operational events. You can
              turn these off in iOS / Android system settings or by signing
              out.
            </li>
            <li>
              To diagnose crashes and performance regressions (Crashlytics).
            </li>
            <li>
              To meet legal, accounting, and audit obligations of the
              regulated trucking industry.
            </li>
          </ul>
          <p>
            We do <strong>not</strong> sell personal information, do
            <strong> not</strong> use it for cross-app or cross-website
            advertising, and do <strong>not</strong> share it with data
            brokers.
          </p>
        </Section>

        <Section id="sharing" title="4. Sharing &amp; disclosure">
          <p>
            <strong>Within your tenant.</strong> Information you submit is
            visible to authorized users of the same trucking company &mdash;
            typically the dispatchers, accountants, document clerks, and
            managers configured by the company owner. Drivers only see their
            own data plus what is shared with them in chat or task
            assignments.
          </p>
          <p>
            <strong>Service providers.</strong> We use a small set of
            sub-processors to operate the Service. Each one is bound by
            written terms restricting them to processing only as needed:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Google Cloud Platform</strong> &mdash; hosting (Cloud
              Run, Compute Engine), database (Cloud SQL), and object storage
              (Cloud Storage).
            </li>
            <li>
              <strong>Firebase</strong> (Google) &mdash; push notifications
              (FCM) and crash reporting (Crashlytics).
            </li>
            <li>
              <strong>Google Maps Platform</strong> &mdash; map tiles,
              geocoding, and directions used by the apps.
            </li>
            <li>
              <strong>Google Identity</strong> &amp;{" "}
              <strong>Apple Sign In</strong> &mdash; optional federated
              sign-in.
            </li>
            <li>
              <strong>Resend</strong> &mdash; transactional email
              (verification, password reset).
            </li>
            <li>
              <strong>Sentry</strong> &mdash; performance and error monitoring
              (mobile SDK temporarily disabled at the date above).
            </li>
            <li>
              <strong>Cloudflare</strong> &mdash; DNS, TLS termination, and
              DDoS protection at the edge.
            </li>
          </ul>
          <p>
            <strong>Legal requests.</strong> We will disclose information when
            required by valid legal process (subpoena, court order, etc.) and
            we will notify the affected tenant unless legally barred from
            doing so.
          </p>
        </Section>

        <Section id="your-choices" title="5. Your choices &amp; rights">
          <p>
            <strong>Access &amp; correction.</strong> You can view and edit
            your profile, phone, address, and documents inside the app at any
            time. Driver accounts also expose vehicle data through
            <span className="font-mono"> Profile → Vehicles</span>.
          </p>

          <p>
            <strong>Delete your account.</strong> You may delete your account
            through any of the following channels:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <em>From the Driver mobile app:</em> open{" "}
              <span className="font-mono">Settings → Delete Account</span> and
              confirm. The action does not require approval from your
              dispatcher.
            </li>
            <li>
              <em>From the web (no login required):</em> visit{" "}
              <a
                className="text-blue-600 underline"
                href="https://www.ealoom.com/account-deletion"
              >
                www.ealoom.com/account-deletion
              </a>
              , enter the email tied to your account, and confirm via the
              verification email we send. The page is publicly accessible and
              linked from the home-page footer.
            </li>
            <li>
              <em>Via email:</em> contact{" "}
              <a
                className="text-blue-600 underline"
                href={`mailto:${PRIVACY_EMAIL}`}
              >
                {PRIVACY_EMAIL}
              </a>{" "}
              from the address tied to your account.
            </li>
          </ul>
          <p>
            <strong>What we delete.</strong> Within 45 days of a confirmed
            request we delete or irreversibly anonymize: your name, email,
            phone number, mailing address, profile photo, social-login
            subjects (Google / Apple), CDL, medical card, TWIC, drug-test,
            insurance, and EIN documents, push notification token, and chat
            message content authored by you. All active sessions on every
            device are invalidated immediately upon request and the email
            address becomes available again for re-registration.
          </p>
          <p>
            <strong>What we retain (and why).</strong> Historical task
            identifiers, payment / invoice records, and audit-log entries
            that reference your account are retained in anonymized form for
            the period required by U.S. trucking regulations (49 CFR §391.51:
            3 years from termination for driver records; IRS recordkeeping:
            7 years for tax records) and so the tenant trucking company can
            complete its books-and-records obligations. After anonymization
            these records contain no personally identifying information.
          </p>
          <p>
            <strong>Push notifications.</strong> Turn them off in iOS /
            Android system settings or in the Driver app drawer.
          </p>
          <p>
            <strong>Location.</strong> Switch the driver status to
            &ldquo;Off Duty&rdquo; in the mobile app or revoke location
            permission in system settings. Tasks already in progress when
            permission is revoked may be marked as paused.
          </p>
        </Section>

        <Section id="retention" title="6. Data retention">
          <ul className="list-disc pl-5 space-y-1">
            <li>Account profile data: while your account is active.</li>
            <li>
              Driver GPS location history: <strong>24 months</strong>, then
              deleted from production systems.
            </li>
            <li>
              Push notification tokens: until you sign out, uninstall, or
              delete your account.
            </li>
            <li>
              Operational records (tasks, invoices, payments): up to 7 years
              after the relevant fiscal year (IRS) or 3 years after driver
              termination (FMCSA), whichever applies.
            </li>
            <li>Crash and diagnostic logs: 90 days.</li>
            <li>
              After account deletion: see Section 5 for the deletion /
              retention split.
            </li>
          </ul>
        </Section>

        <Section id="security" title="7. Security">
          <p>
            All traffic between the apps and our backend is encrypted with
            TLS. Passwords are hashed with bcrypt; documents and photos are
            stored encrypted at rest in Google Cloud Storage. Refresh tokens
            are stored in iOS Keychain / Android Keystore via{" "}
            <span className="font-mono">flutter_secure_storage</span>. We use
            short-lived access JWTs with a token-version revocation column so
            that deleting or disabling an account immediately invalidates
            sessions on every device.
          </p>
        </Section>

        <Section id="children" title="8. Children and age requirements">
          <p>
            The Service is intended for use by trucking professionals.
            Drivers must be <strong>21 years or older</strong> to register,
            consistent with U.S. Federal Motor Carrier Safety Administration
            rules for interstate commercial driving (49 CFR §391.11). We do
            not knowingly collect information from anyone under 13. If you
            believe a minor has registered, contact{" "}
            <a className="text-blue-600 underline" href={`mailto:${PRIVACY_EMAIL}`}>
              {PRIVACY_EMAIL}
            </a>{" "}
            and we will remove the account.
          </p>
        </Section>

        <Section id="international" title="9. International users">
          <p>
            {COMPANY_BRAND} is operated from the United States and our
            infrastructure is hosted in the{" "}
            <span className="font-mono">us-west1</span> Google Cloud region.
            If you access the Service from outside the U.S., you consent to
            the transfer of your information to the United States, which may
            have data-protection laws different from those in your country.
          </p>
        </Section>

        <Section
          id="browser-extension"
          title="10. Browser extension (Container Monitor)"
        >
          <p>
            The {COMPANY_BRAND} Container Monitor is an optional Chrome
            extension that bridges the {COMPANY_BRAND} dispatch console with
            the marine-terminal websites used by Oakland-area trucking
            operations. It is installed from the Chrome Web Store by a
            dispatcher and bound to a single {COMPANY_BRAND} tenant account.
          </p>
          <p>
            <strong>What it does.</strong>
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Polls SSA (<span className="font-mono">b58.tideworks.com</span>),
              ETS (<span className="font-mono">www.etslink.com</span>), and
              TraPac (<span className="font-mono">oakland.trapac.com</span>) on
              a randomized schedule for container release / hold status, and
              pushes the parsed status into the bound {COMPANY_BRAND} tenant.
            </li>
            <li>
              When an operator initiates a booking, plate update, reschedule,
              or on-hire action from {COMPANY_BRAND}, the extension pre-fills
              the corresponding form on{" "}
              <span className="font-mono">termops.emodal.com</span> or{" "}
              <span className="font-mono">support.cosco-usa.com</span> with
              data the operator already entered in {COMPANY_BRAND}.
            </li>
          </ul>
          <p>
            <strong>What it collects and where it sends data.</strong>
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Enrollment token (JWT)</strong> &mdash; stored locally in
              extension storage; sent to{" "}
              <span className="font-mono">api.ealoom.com</span> on every
              backend request to authenticate the extension to its bound
              tenant.
            </li>
            <li>
              <strong>Container numbers, terminal codes, parsed status</strong>{" "}
              &mdash; the operational data the operator enrolled the extension
              to fetch. Sent to{" "}
              <span className="font-mono">api.ealoom.com</span> and stored in
              the bound tenant&rsquo;s account.
            </li>
            <li>
              <strong>Terminal session credentials.</strong> The extension
              passively captures the session keys / cookies produced by the
              operator&rsquo;s existing logged-in session on the terminal
              websites and uses them to call the terminals&rsquo; own APIs.
              These credentials are transmitted to{" "}
              <span className="font-mono">api.ealoom.com</span> solely so the
              backend can retrieve container data on the operator&rsquo;s
              behalf. They are not retained beyond their session validity. The
              extension does <strong>not</strong> capture terminal-website
              passwords.
            </li>
            <li>
              <strong>Assist-action outcomes</strong> &mdash; success / failure
              status of the operator-initiated form-fill actions, sent to{" "}
              <span className="font-mono">api.ealoom.com</span> so the
              dispatcher can confirm completion.
            </li>
            <li>
              <strong>Diagnostic data</strong> &mdash; crash stack traces and
              page URLs (query parameters scrubbed) sent to{" "}
              <span className="font-mono">Sentry</span> to diagnose extension
              crashes. No PII, no container data, no session credentials are
              attached.
            </li>
          </ul>
          <p>
            <strong>What it does not do.</strong>
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Does <strong>not</strong> read or transmit data from any site
              outside the five domains listed above (SSA, ETS, TraPac, eModal,
              COSCO) plus {COMPANY_BRAND}&rsquo;s own domains.
            </li>
            <li>
              Does <strong>not</strong> request{" "}
              <span className="font-mono">&lt;all_urls&gt;</span> or any
              wildcard host permission.
            </li>
            <li>
              Does <strong>not</strong> contain remote code: all JavaScript is
              bundled in the published package; no{" "}
              <span className="font-mono">eval</span>, no remote-loaded
              scripts.
            </li>
            <li>
              Does <strong>not</strong> sell, share, or transfer user data to
              third parties for purposes unrelated to the extension&rsquo;s
              single purpose.
            </li>
          </ul>
          <p>
            <strong>Your control.</strong> You can disable or remove the
            extension at any time from{" "}
            <span className="font-mono">chrome://extensions</span>. To revoke
            the enrollment server-side, sign in to {COMPANY_BRAND} and remove
            the extension binding from your dispatcher account settings; the
            backend will reject any further requests from that enrollment
            token.
          </p>
        </Section>

        <Section id="changes" title="11. Changes to this policy">
          <p>
            We will update the effective date and version number at the top
            of this page each time the substance of this policy changes. For
            material changes we will also email the account owner of each
            tenant and, for mobile users, surface an in-app notice at next
            sign-in.
          </p>
        </Section>

        <Section id="contact" title="12. Contact">
          <p className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-slate-500" />
            <a className="text-blue-600 underline" href={`mailto:${PRIVACY_EMAIL}`}>
              {PRIVACY_EMAIL}
            </a>
          </p>
          <p>
            {COMPANY_LEGAL_NAME} (d/b/a {COMPANY_BRAND})
            <br />
            Attn: Privacy
            <br />
            {POSTAL_ADDRESS_LINE_1}
            <br />
            {POSTAL_ADDRESS_LINE_2}
            <br />
            {POSTAL_ADDRESS_COUNTRY}
          </p>
        </Section>

        <footer className="mt-12 pt-6 border-t border-slate-200 text-sm text-slate-500">
          <Link to="/support" className="text-blue-600 hover:underline">
            Need help? Contact support →
          </Link>
        </footer>
      </div>
    </div>
  );
}

export default Privacy;
